import { useFormik } from "formik";
import React from "react";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";
import * as Yup from "yup";
import { useAddAdmin } from "../../apis/admins/add-admin";
import { ADMIN_TYPES } from "../../utils/Enums";
import { IAddAdmins } from "../../utils/types";
import Button from "../Button";
import Input from "../Input";
import Modal from "../Modal";
import PasswordInput from "../PasswordInput";

interface PropTypes {
  isVisible: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const adminOptions = [
  { label: "Admin", value: ADMIN_TYPES.ADMIN },
  { label: "Super admin", value: ADMIN_TYPES.SUPER_ADMIN },
];

const AddAdminModal = ({ isVisible, onHide, onSuccess }: PropTypes) => {
  const { mutateAsync: addAdmin, isPending: isLoading } = useAddAdmin();

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      userType: ADMIN_TYPES.ADMIN,
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().trim().required("This field is required"),
      lastName: Yup.string().trim().required("This field is required"),
      email: Yup.string().trim().email().required("This field is required"),
      password: Yup.string().trim().min(6, "Password must be at least 6 characters").required("This field is required"),
      userType: Yup.string().required("Please select a user type"),
    }),
    onSubmit: async (values: IAddAdmins) => {
      try {
        const res = await addAdmin(values);
        toast.success(res?.message);
        onSuccess();
        onHide();
        formik.resetForm();
      } catch (error: any) {
        toast.error(error?.response?.data?.error || "Something went wrong");
      }
    },
  });

  return (
    <Modal
      isVisible={isVisible}
      onHide={() => { formik.resetForm(); onHide(); }}
      heading="Add admin"
    >
      <form onSubmit={formik.handleSubmit} className="md:grid md:grid-cols-2 md:gap-4 px-8">
        <Input
          name="firstName"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="First name *"
          className="input-primary"
          placeholder="Enter first name"
          isInvalid={Boolean(formik.touched.firstName && formik.errors.firstName)}
          error={formik.errors.firstName}
        />
        <Input
          name="lastName"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Last name *"
          className="input-primary"
          placeholder="Enter last name"
          isInvalid={Boolean(formik.touched.lastName && formik.errors.lastName)}
          error={formik.errors.lastName}
        />
        <Input
          name="email"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Email *"
          autoComplete="off"
          type="email"
          className="input-primary"
          placeholder="Enter email"
          isInvalid={Boolean(formik.touched.email && formik.errors.email)}
          error={formik.errors.email}
        />
        <PasswordInput
          name="password"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Password *"
          className="input-primary text-[1rem] lg:text-xs"
          placeholder="Enter password"
          autoComplete="new-password"
          isInvalid={Boolean(formik.touched.password && formik.errors.password)}
          error={formik.errors.password}
        />
        <div className="col-span-2 mt-4">
          <div className="join w-full">
            {adminOptions.map((option, index) => (
              <Button
                key={option.value + index}
                type="button"
                onClick={() => formik.setFieldValue("userType", option.value)}
                className={twMerge(
                  "join-item w-1/2 bg-white text-primary py-2",
                  formik.values.userType === option.value && "!bg-primary !text-white"
                )}
              >
                {option.label}
              </Button>
            ))}
          </div>
          {formik.touched.userType && formik.errors.userType && (
            <div className="label">
              <span className="label-text-alt text-red-600">{formik.errors.userType}</span>
            </div>
          )}
        </div>
        <div className="col-span-2 text-center mt-4 flex justify-center">
          <Button isLoading={isLoading} type="submit" className="w-full md:w-32 min-h-0 h-10">
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddAdminModal;